<script lang="ts">
  // Deck Editor, Master Duel-style 3-pane: a large card preview rail (left), the
  // deck as full-art card grids Main/Extra/Side (centre), and the card pool as a
  // grid with a labelled filter bar (right). Default scope is the player's collection
  // (fetched once, filtered locally); an "All Cards" toggle opens the full ~14k
  // catalog via TanStack Query (on-demand, 24h cache).
  import { onMount } from "svelte";
  import { fly } from "svelte/transition";
  import { createQuery } from "@tanstack/svelte-query";
  import type { CardInfo } from "@ygoevo/yo2-data";
  import type { CardDismantleItem, DeckSummary } from "@ygoevo/world-protocol";
  import { go, session } from "../stores/session.svelte.ts";
  import { send, listen } from "../stores/net.svelte.ts";
  import { searchCards, cardsByIds, searchKey, byIdKey, filterCards, typeLine, type SearchParams } from "../cards.ts";
  import { craftCost, dismantleYield, rarityLabel, rarityColor, foilColor, foilLabel, foilDismantleYield } from "../rarity.ts";
  import DataWindow from "../components/ui/DataWindow.svelte";
  import NeonButton from "../components/ui/NeonButton.svelte";
  import Field from "../components/ui/Field.svelte";
  import Toast, { type ToastKind } from "../components/ui/Toast.svelte";
  import CardTile from "../components/CardTile.svelte";
  import CardThumb from "../components/CardThumb.svelte";
  import { createDnd, type DeckZone, type DragOrigin } from "../dnd.svelte.ts";
  import { draggableDialog } from "../actions/draggableDialog.ts";
  import { cardImage } from "../../data.ts";
  import { parseDeck, toYdk, toYdke } from "../ydk.ts";
  import gxpacksData from "@ygoevo/yo2-data/generated/gxpacks.json";
  import aliasesData from "@ygoevo/yo2-data/generated/aliases.json";

  // Alt-art / reprint passcode → canonical (oldest-art) passcode. Exported decks
  // (EDOPro / YGOPRODeck) routinely use an alt-art passcode — e.g. 18144507 for
  // Harpie's Feather Duster — but our catalog is canonical-only, so we fold every
  // imported id onto its canonical before validating + storing the deck.
  const ALIASES = aliasesData.aliases as Record<string, number>;
  const canonId = (id: number): number => ALIASES[String(id)] ?? id;

  const reduced = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Which mode's deck we're editing. Free Duel ("free") edits a SEPARATE deck namespace, lifts the
  // ownership gate (every card buildable, capped at 3 copies), and hides the craft/dismantle economy
  // — but still shows the foils the player earned in the World on cards they own a foil of.
  const fmt = $derived(session.mode);
  const isFree = $derived(fmt === "free");
  const homeRoute = $derived(fmt === "free" ? ("freeduel" as const) : ("world" as const));

  // ── filters / scope ──────────────────────────────────────────────────────────
  let q = $state("");
  let cat = $state("");
  let attr = $state("");
  let race = $state("");
  let type = $state("");
  let level = $state<number | "">("");
  let sortBy = $state<"name" | "atk" | "level" | "rarity">("name");
  let limit = $state(96);
  // Free Duel has no "collection", so default the pool to the full catalog there.
  let viewAll = $state(session.mode === "free");

  // Picking a category resets the sub-filters: attribute/race/level only apply to monsters,
  // and the Type list differs per category, so a leftover filter would otherwise silently
  // zero the results (the main "confusing filters" trap).
  function pickCat(v: string) {
    cat = v;
    attr = "";
    race = "";
    type = "";
    level = "";
  }

  const CATS = [
    { v: "", label: "All" },
    { v: "monster", label: "Monster" },
    { v: "monster-extra", label: "Extra" },
    { v: "spell", label: "Spell" },
    { v: "trap", label: "Trap" },
  ];
  // Filter vocabularies (values match the catalog exactly — see apps/world card data).
  const ATTRS = ["DARK", "LIGHT", "EARTH", "WATER", "FIRE", "WIND", "DIVINE"];
  const RACES = [
    "Aqua", "Beast", "Beast-Warrior", "Creator God", "Cyberse", "Dinosaur", "Divine-Beast",
    "Dragon", "Fairy", "Fiend", "Fish", "Illusion", "Insect", "Machine", "Plant", "Psychic",
    "Pyro", "Reptile", "Rock", "Sea Serpent", "Spellcaster", "Thunder", "Warrior",
    "Winged Beast", "Wyrm", "Zombie",
  ];
  // The Type filter swaps its options to match the chosen category (monster card type vs
  // spell/trap subtype). "All"/monster categories use the monster set.
  const MONSTER_TYPES = ["Normal", "Effect", "Fusion", "Synchro", "Xyz", "Link", "Pendulum", "Ritual", "Tuner", "Flip", "Toon", "Spirit", "Union", "Gemini"];
  const SPELL_TYPES = ["Normal", "Continuous", "Quick-Play", "Equip", "Field", "Ritual"];
  const TRAP_TYPES = ["Normal", "Continuous", "Counter"];
  const isMonsterCat = $derived(cat === "" || cat === "monster" || cat === "monster-extra");
  const typeOpts = $derived(cat === "spell" ? SPELL_TYPES : cat === "trap" ? TRAP_TYPES : MONSTER_TYPES);
  // Consistent neon-select styling; per-select active colour added at the call site.
  const selectCls =
    "chamfer-btn min-w-[5.25rem] bg-void-2/70 px-2 py-1.5 font-display text-[0.64rem] uppercase tracking-wide outline-none transition-colors hover:bg-surface-2";

  // ── inventory + saved decks (over WS) ────────────────────────────────────────
  // Plain copies and foil copies (per grade) are tracked separately, same card,
  // separate collectibles. `ownedQty` is the total (plain + all foils) used for deck
  // legality + the owned/used count; `bestFoil` drives the holographic finish shown.
  let plain = $state<Record<number, number>>({});
  let foils = $state<Record<number, Record<number, number>>>({});
  // Of the plain copies, how many are SOUL-BOUND (free starter-deck cards): they count for deck
  // legality but can't be dismantled or traded. Tradeable/dismantleable plain = plainQty − bound.
  let bound = $state<Record<number, number>>({});
  let decks = $state<DeckSummary[]>([]);
  listen("Inventory", (m) => {
    const p: Record<number, number> = {};
    const f: Record<number, Record<number, number>> = {};
    const b: Record<number, number> = {};
    for (const it of m.items) {
      if (it.foil) (f[it.cardId] ??= {})[it.foil] = it.qty;
      else { p[it.cardId] = it.qty; if (it.bound) b[it.cardId] = it.bound; }
    }
    plain = p;
    foils = f;
    bound = b;
  });
  listen("Decks", (m) => {
    if ((m.format ?? "world") !== fmt) return; // only react to this editor's mode (World vs Free Duel)
    decks = m.decks;
    if (pendingSave) {
      pendingSave = false;
      flash("success", "Deck saved ✓");
    }
  });

  // ── shards: craft / dismantle (Master-Duel-style) ───────────────────────────
  const shards = $derived(session.loginOk?.wallet.shards ?? 0);
  let working = $state(false); // a craft/dismantle round-trip is in flight
  let dismantleMode = $state(false);
  let bulkFinish = $state(0); // 0 = standard/plain, 1..3 = a specific foil grade
  let bulkSelection = $state<Record<string, CardDismantleItem>>({});
  let bulkConfirm = $state(false);
  let extrasConfirm = $state(false);

  const FINISHES = [
    { grade: 0, label: "Standard" },
    { grade: 1, label: "Holo foil" },
    { grade: 2, label: "Prismatic foil" },
    { grade: 3, label: "Galaxy foil" },
  ];

  function applyFinishUpdate(m: { cardId: number; owned: number; foil?: number }) {
    const grade = m.foil ?? 0;
    if (grade) {
      const g = { ...(foils[m.cardId] ?? {}) };
      if (m.owned > 0) g[grade] = m.owned;
      else delete g[grade];
      if (Object.keys(g).length) foils = { ...foils, [m.cardId]: g };
      else { const { [m.cardId]: _drop, ...rest } = foils; foils = rest; }
    } else if (m.owned > 0) {
      plain = { ...plain, [m.cardId]: m.owned };
    } else {
      const { [m.cardId]: _drop, ...rest } = plain;
      plain = rest;
    }
  }

  listen("CardUpdate", (m) => {
    working = false;
    applyFinishUpdate(m);
    if (session.loginOk) session.loginOk.wallet = m.wallet; // sync shard balance
  });
  listen("CardsDismantled", (m) => {
    working = false;
    for (const update of m.updates) applyFinishUpdate(update);
    if (session.loginOk) session.loginOk.wallet = m.wallet;
    bulkSelection = {};
    bulkConfirm = false;
    extrasConfirm = false;
    flash(
      m.copies > 0 ? "success" : "info",
      m.copies > 0
        ? `Dismantled ${m.copies} ${m.copies === 1 ? "copy" : "copies"} · +◆ ${m.shardsGained.toLocaleString()}`
        : "No extra standard copies — every card is already at three or fewer.",
    );
  });
  listen("Error", (m) => {
    // Only react to errors from one of THIS screen's in-flight actions (craft/dismantle, save),
    // otherwise a background error would pop a spurious toast. Clearing the pending flag is what
    // surfaces a rejected DeckSave; without it the failure is silent and the stale flag later
    // mis-flashes "saved ✓" on the next unrelated Decks push.
    if (!working && !pendingSave) return;
    working = false;
    pendingSave = false;
    bulkConfirm = false;
    extrasConfirm = false;
    flash("error", m.message || "Couldn't do that");
  });
  function craft(c: CardInfo) {
    if (working || !craftable(c.id) || ownedQty(c.id) >= 3 || shards < craftCost(c.rarity)) return;
    working = true;
    send({ t: "CardCraft", cardId: c.id });
  }
  function dismantle(c: CardInfo) {
    if (working || dismantleablePlain(c.id) <= 0) return; // soul-bound starter copies can't be scrapped
    working = true;
    send({ t: "CardDismantle", cardId: c.id, qty: 1 });
  }
  function dismantleFoil(c: CardInfo, grade: number) {
    if (working || ownedFoilQty(c.id, grade) <= 0) return;
    working = true;
    send({ t: "CardDismantle", cardId: c.id, qty: 1, foil: grade });
  }

  // plain vs foil vs total. ownedQty (total) gates deck legality + the count badge;
  // bestFoil (highest grade owned) drives the holographic finish shown on a card.
  const plainQty = (id: number) => plain[id] ?? 0;
  const boundQty = (id: number) => bound[id] ?? 0;
  /** Plain copies that may be dismantled = plain owned − soul-bound starter copies. */
  const dismantleablePlain = (id: number) => Math.max(0, plainQty(id) - boundQty(id));
  const foilMap = (id: number) => foils[id] ?? {};
  const ownedFoilQty = (id: number, grade: number) => foilMap(id)[grade] ?? 0;
  const foilTotal = (id: number) => Object.values(foilMap(id)).reduce((s, n) => s + n, 0);
  const ownedQty = (id: number) => plainQty(id) + foilTotal(id);
  const ownedGrades = (id: number) =>
    Object.keys(foilMap(id)).map(Number).filter((g) => foilMap(id)[g]! > 0).sort((a, b) => a - b);
  const bestFoil = (id: number) => { const gs = ownedGrades(id); return gs.length ? gs[gs.length - 1]! : 0; };
  const ownedIds = $derived(
    [...new Set([...Object.keys(plain), ...Object.keys(foils)].map(Number))].filter((id) => ownedQty(id) > 0),
  );

  // World deck-era gate (for import): the World is set in the GX era, i.e. the 27 GX-era boosters in
  // gxpacks.json. A card is in-era if it's in that pool, or already in your collection (covers
  // starter/granted cards, which are era-appropriate by construction). Free Duel ignores era.
  // ponytail: era pool = GX boosters ∪ owned; if World ever grants cards from outside that pool,
  // source the in-era set from the server instead of bundling gxpacks.json here.
  const ERA_LABEL = "GX";
  const GX_POOL = new Set<number>((gxpacksData as { packs: { cards: number[] }[] }).packs.flatMap((p) => p.cards));
  const inEra = (id: number) => GX_POOL.has(id) || ownedQty(id) > 0;
  // Craftable = strictly in the GX pack pool. NO owned-escape here: owning a post-GX card (e.g. one
  // held from before this gate, or via trade) must NOT let you mint more. Mirrors the server's
  // isCraftable so the button never offers a craft the server will reject.
  const craftable = (id: number) => GX_POOL.has(id);

  // ── card sources ──────────────────────────────────────────────────────────────
  const ownedQuery = createQuery(() => ({
    queryKey: byIdKey(ownedIds),
    queryFn: () => cardsByIds(ownedIds),
    enabled: ownedIds.length > 0,
  }));
  const ownedCards = $derived(ownedQuery.data ?? []);

  const params = $derived<SearchParams>({
    q: q.trim() || undefined,
    cat: cat || undefined,
    attr: attr || undefined,
    race: race || undefined,
    type: type || undefined,
    level: level === "" ? undefined : level,
    offset: 0,
    limit,
  });
  $effect(() => {
    void [q, cat, attr, race, type, level, viewAll];
    limit = 96;
  });
  const catalog = createQuery(() => ({ queryKey: searchKey(params), queryFn: () => searchCards(params), enabled: viewAll }));

  const results = $derived(
    viewAll
      ? (catalog.data?.cards ?? [])
      : filterCards(ownedCards, {
          q,
          cat: cat || undefined,
          attr: attr || undefined,
          race: race || undefined,
          type: type || undefined,
          level: level === "" ? undefined : level,
        }),
  );
  const loading = $derived(viewAll ? catalog.isPending : ownedIds.length > 0 && ownedQuery.isPending);
  const errored = $derived(viewAll ? catalog.isError : ownedQuery.isError);

  // Pool sort. Rarity ranks high→low (= shard value, since craft cost scales with rarity).
  // ponytail: in "All Cards" this only reorders the loaded page (catalog is server-paginated);
  // the collection scope is fully in-memory so it sorts the whole set.
  const RARITY_RANK: Record<string, number> = { UR: 3, SR: 2, R: 1, N: 0 };
  const sorted = $derived.by(() => {
    if (sortBy === "name") return results;
    const r = [...results];
    const byName = (a: CardInfo, b: CardInfo) => a.name.localeCompare(b.name);
    if (sortBy === "atk") r.sort((a, b) => (b.atk ?? -1) - (a.atk ?? -1) || byName(a, b));
    else if (sortBy === "level") r.sort((a, b) => (b.level ?? -1) - (a.level ?? -1) || byName(a, b));
    else r.sort((a, b) => RARITY_RANK[b.rarity ?? "N"]! - RARITY_RANK[a.rarity ?? "N"]! || byName(a, b));
    return r;
  });

  // ── card cache (deck grids + preview render from this) ──────────────────────
  let cache = $state<Record<number, CardInfo>>({});
  $effect(() => {
    for (const c of [...(catalog.data?.cards ?? []), ...ownedCards]) cache[c.id] = c;
  });
  const card = (id: number): CardInfo | undefined => cache[id];

  const selectionKey = (id: number, foil = bulkFinish) => `${id}:${foil}`;
  const selectedQty = (id: number, foil = bulkFinish) => bulkSelection[selectionKey(id, foil)]?.qty ?? 0;
  const finishCapacity = (id: number, foil = bulkFinish) =>
    foil ? ownedFoilQty(id, foil) : dismantleablePlain(id);
  const finishLabel = (foil = bulkFinish) => FINISHES.find((f) => f.grade === foil)?.label ?? "Standard";

  function setBulkQty(id: number, foil: number, requested: number) {
    const key = selectionKey(id, foil);
    const qty = Math.max(0, Math.min(Math.floor(requested), finishCapacity(id, foil)));
    const next = { ...bulkSelection };
    if (qty <= 0) delete next[key];
    else next[key] = { cardId: id, qty, ...(foil ? { foil } : {}) };
    bulkSelection = next;
    bulkConfirm = false;
    extrasConfirm = false;
  }

  function toggleBulk(c: CardInfo) {
    detail = c;
    const current = selectedQty(c.id);
    setBulkQty(c.id, bulkFinish, current > 0 ? 0 : 1);
  }

  function addBulkCopy(id: number, foil: number) {
    setBulkQty(id, foil, selectedQty(id, foil) + 1);
  }

  function toggleDismantleMode() {
    dismantleMode = !dismantleMode;
    viewAll = false;
    bulkFinish = 0;
    bulkSelection = {};
    bulkConfirm = false;
    extrasConfirm = false;
  }

  function showAllCards() {
    if (dismantleMode) toggleDismantleMode();
    viewAll = true;
  }

  function chooseBulkFinish(grade: number) {
    bulkFinish = grade;
    bulkConfirm = false;
    extrasConfirm = false;
  }

  const bulkItems = $derived(Object.values(bulkSelection));
  const bulkCopies = $derived(bulkItems.reduce((sum, item) => sum + item.qty, 0));
  const bulkCardCount = $derived(new Set(bulkItems.map((item) => item.cardId)).size);
  const bulkShardGain = $derived.by(() =>
    bulkItems.reduce((sum, item) => {
      const rarity = card(item.cardId)?.rarity;
      return sum + item.qty * (item.foil ? foilDismantleYield(rarity, item.foil) : dismantleYield(rarity));
    }, 0),
  );
  const extraItems = $derived.by(() =>
    ownedIds
      .map((id) => ({ cardId: id, qty: Math.max(0, plainQty(id) - Math.max(boundQty(id), 3)) }))
      .filter((item) => item.qty > 0),
  );
  const extraCopies = $derived(extraItems.reduce((sum, item) => sum + item.qty, 0));
  const extraShardGain = $derived(
    extraItems.reduce((sum, item) => sum + item.qty * dismantleYield(card(item.cardId)?.rarity), 0),
  );

  function submitBulkDismantle() {
    if (working || bulkItems.length === 0) return;
    working = true;
    send({ t: "CardDismantleBulk", items: bulkItems });
  }

  function submitExtraDismantle() {
    if (working || extraCopies <= 0) return;
    working = true;
    send({ t: "CardDismantleExtras" });
  }

  // ── the deck being built ─────────────────────────────────────────────────────
  let main = $state<number[]>([]);
  let extra = $state<number[]>([]);
  let side = $state<number[]>([]);
  let deckName = $state("New Deck");
  let detail = $state<CardInfo | null>(null);

  const count = (id: number) => [...main, ...extra, ...side].filter((x) => x === id).length;
  // Max copies allowed: the TCG 3-copy cap, further limited by how many you own in World mode. Free
  // Duel ignores ownership entirely (every card is buildable, up to 3).
  const copyCap = (id: number) => (isFree ? 3 : Math.min(3, ownedQty(id)));
  const canAddCopy = (id: number) => count(id) < copyCap(id);
  const copyViolation = $derived.by(() => {
    const m = new Map<number, number>();
    for (const id of [...main, ...extra, ...side]) m.set(id, (m.get(id) ?? 0) + 1);
    for (const [id, n] of m) if (n > 3 || (!isFree && ownedQty(id) < n)) return true;
    return false;
  });
  const legal = $derived(
    main.length >= 40 && main.length <= 60 && extra.length <= 15 && side.length <= 15 &&
      !copyViolation && deckName.trim().length > 0,
  );
  // Main-deck composition (Master Duel shows the M/S/T split).
  const split = $derived.by(() => {
    let m = 0, s = 0, t = 0;
    for (const id of main) {
      const c = card(id);
      if (!c) continue;
      if (c.category === "spell") s++;
      else if (c.category === "trap") t++;
      else m++;
    }
    return { m, s, t };
  });

  // Click / Enter adds to Main (or Extra for extra-deck monsters); the Side deck is filled by
  // dragging a card onto it.
  function add(c: CardInfo) {
    if (!canAddCopy(c.id)) return;
    cache[c.id] = c;
    if (c.category === "monster-extra") {
      if (extra.length < 15) extra.push(c.id);
    } else if (main.length < 60) main.push(c.id);
  }
  const removeAt = (zone: number[], i: number) => zone.splice(i, 1);
  // Right-click a pool card to pull one copy back out of the deck (checks Main, then Extra, then Side).
  function removeOne(id: number) {
    for (const arr of [main, extra, side]) {
      const i = arr.lastIndexOf(id);
      if (i >= 0) { arr.splice(i, 1); return; }
    }
  }

  // ── drag-and-drop (pool ⇄ deck, reorder, move between zones) ────────────────
  const zoneArr = (z: DeckZone) => (z === "main" ? main : z === "extra" ? extra : side);
  const zoneMax = (z: DeckZone) => (z === "main" ? 60 : 15);
  // Frame rules: Extra holds only Extra-deck monsters; Main holds everything else;
  // Side takes anything. (Copy/ownership limits are enforced on insert.)
  const canAccept = (c: CardInfo, z: DeckZone) =>
    z === "extra" ? c.category === "monster-extra" : z === "main" ? c.category !== "monster-extra" : true;

  // Last click on a deck slot, so a second click on the same slot within 400ms removes it.
  let lastDeckClick = { zone: "", index: -1, t: 0 };
  const dnd = createDnd({
    canAccept,
    add(c, z, i) {
      if (!canAccept(c, z)) return;
      if (!canAddCopy(c.id)) return;
      const arr = zoneArr(z);
      if (arr.length >= zoneMax(z)) return;
      cache[c.id] = c;
      arr.splice(Math.max(0, Math.min(i, arr.length)), 0, c.id);
    },
    move(from, to) {
      const src = zoneArr(from.zone);
      const id = src[from.index];
      if (id == null) return;
      const c = card(id);
      if (c && !canAccept(c, to.zone)) return;
      if (from.zone !== to.zone && zoneArr(to.zone).length >= zoneMax(to.zone)) return;
      src.splice(from.index, 1);
      const dst = zoneArr(to.zone);
      dst.splice(Math.max(0, Math.min(to.index, dst.length)), 0, id);
    },
    remove(z, i) {
      zoneArr(z).splice(i, 1);
    },
    click(origin, c) {
      // Click a pool card to add it. A single click on a DECK card only inspects it — a stray
      // click shouldn't yank a card out — so removal needs a double-click (right-click / drag to
      // the pool still remove in one action).
      detail = c;
      if (origin.kind === "pool") { add(c); return; }
      const now = Date.now();
      const dbl = lastDeckClick.zone === origin.zone && lastDeckClick.index === origin.index && now - lastDeckClick.t < 400;
      if (dbl) {
        removeAt(zoneArr(origin.zone), origin.index);
        lastDeckClick = { zone: "", index: -1, t: 0 };
      } else {
        lastDeckClick = { zone: origin.zone, index: origin.index, t: now };
      }
    },
  });
  const poolOrigin: DragOrigin = { kind: "pool" };

  // Auto-sort each zone: frame group → level desc → name (Master Duel "Sort").
  function sortZone(arr: number[]): number[] {
    const rank = (id: number) => {
      const c = card(id);
      if (!c) return [9, 0, ""] as const;
      const g = c.category === "spell" ? 1 : c.category === "trap" ? 2 : 0;
      return [g, -(c.level ?? 0), c.name] as const;
    };
    return [...arr].sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      return ra[0] - rb[0] || ra[1] - rb[1] || String(ra[2]).localeCompare(String(rb[2]));
    });
  }
  function autoSort() {
    main = sortZone(main);
    extra = sortZone(extra);
    side = sortZone(side);
  }

  // ── save / load decks ─────────────────────────────────────────────────────────
  // No "equip": the deck a player duels with is chosen per-duel in the deck picker shown before
  // entering a duel (see DeckChooser), so the editor only builds + saves decks.
  let pendingSave = false;
  let toast = $state<{ kind: ToastKind; text: string } | null>(null);
  let toastTimer: ReturnType<typeof setTimeout>;
  function flash(kind: ToastKind, msg: string) {
    toast = { kind, text: msg };
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (toast = null), 2200);
  }
  function save() {
    if (!legal) return;
    pendingSave = true;
    send({ t: "DeckSave", name: deckName.trim(), main: [...main], extra: [...extra], side: [...side], format: fmt });
  }
  async function loadDeck(name: string) {
    const d = decks.find((x) => x.name === name);
    if (!d) return;
    deckName = d.name;
    main = [...d.main];
    extra = [...(d.extra ?? [])];
    side = [...(d.side ?? [])];
    const missing = [...new Set([...main, ...extra, ...side])].filter((id) => !cache[id]);
    if (missing.length) for (const c of await cardsByIds(missing)) cache[c.id] = c;
  }
  function newDeck() {
    if (main.length + extra.length + side.length > 0 && !confirm("Start a new deck? The current one will be cleared (saved decks are kept).")) return;
    deckName = "New Deck";
    main = [];
    extra = [];
    side = [];
  }

  // ── import / export (.ydk file + ydke:// URL) ───────────────────────────────
  let io = $state<null | "import" | "export">(null);
  let importText = $state("");
  let importErr = $state("");
  let importName = $state(""); // filename of a loaded .ydk, used as the deck name
  const exportYdk = $derived(toYdk({ main, extra, side }));
  const deckIsEmpty = $derived(main.length + extra.length + side.length === 0);

  function openImport() {
    io = "import";
    importText = "";
    importErr = "";
    importName = "";
  }
  async function onYdkFile(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ""; // allow re-selecting the same file
    if (!file) return;
    importText = await file.text();
    importName = file.name.replace(/\.ydk$/i, "");
  }
  function nameList(ids: number[], by: Map<number, CardInfo>): string {
    const names = ids.map((id) => by.get(id)?.name ?? `#${id}`);
    return names.slice(0, 3).join(", ") + (names.length > 3 ? ` +${names.length - 3} more` : "");
  }
  async function doImport() {
    importErr = "";
    const raw = parseDeck(importText);
    // Fold alt-art passcodes onto canonical up front, so validation + storage are canonical.
    const parsed = {
      main: raw.main.map(canonId),
      extra: raw.extra.map(canonId),
      side: raw.side.map(canonId),
    };
    const ids = [...new Set([...parsed.main, ...parsed.extra, ...parsed.side])];
    if (ids.length === 0) {
      importErr = "No cards found. Paste a .ydk list / ydke:// URL, or load a .ydk file.";
      return;
    }
    let infos: CardInfo[];
    try {
      infos = await cardsByIds(ids);
    } catch {
      importErr = "Couldn't validate cards, try again.";
      return;
    }
    const by = new Map(infos.map((c) => [c.id, c]));
    const unknown = ids.filter((id) => !by.has(id));
    // World-only: cards from beyond the era we're set in are rejected outright. Free Duel allows anything.
    const offEra = isFree ? [] : ids.filter((id) => by.has(id) && !inEra(id));
    if (offEra.length || unknown.length) {
      const parts: string[] = [];
      if (offEra.length) parts.push(`${offEra.length} card${offEra.length > 1 ? "s" : ""} beyond the ${ERA_LABEL} era (${nameList(offEra, by)})`);
      if (unknown.length) parts.push(`${unknown.length} unrecognized card${unknown.length > 1 ? "s" : ""}`);
      importErr = `Can't import: ${parts.join("; ")}.`;
      return;
    }
    for (const c of infos) cache[c.id] = c;
    // Trust the .ydk sections, but defensively route any Extra-deck monster that landed in #main.
    const m: number[] = [], x: number[] = [], s: number[] = [];
    for (const id of parsed.main) (by.get(id)!.category === "monster-extra" ? x : m).push(id);
    for (const id of parsed.extra) x.push(id);
    for (const id of parsed.side) s.push(id);
    main = m;
    extra = x;
    side = s;
    if (importName.trim()) deckName = importName.trim();
    io = null;
    flash("success", "Deck imported ✓");
  }
  function copyExport(kind: "ydk" | "ydke") {
    void navigator.clipboard?.writeText(kind === "ydke" ? toYdke({ main, extra, side }) : exportYdk);
    flash("success", "Copied ✓");
  }
  function downloadYdk() {
    const blob = new Blob([exportYdk], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${deckName.trim() || "deck"}.ydk`;
    a.click();
    URL.revokeObjectURL(url);
  }

  onMount(() => {
    send({ t: "InventoryGet" }); // foils + collection still load in Free Duel (foils carry over)
    send({ t: "DeckList", format: fmt });
  });

  const ZONES = $derived([
    { key: "main" as const, label: "Main", arr: main, min: 40, max: 60 },
    { key: "extra" as const, label: "Extra", arr: extra, min: 0, max: 15 },
    { key: "side" as const, label: "Side", arr: side, min: 0, max: 15 },
  ]);
  const fmtVal = (v: number | null) => (v == null ? "" : v === -2 ? "?" : String(v));
</script>

<div class="deck-editor-shell flex h-full w-full flex-col gap-3 p-[clamp(0.5rem,1.6vw,1.1rem)]" style="background: var(--df-bg-deck, transparent)">
  <header class="deck-editor-header flex shrink-0 items-center justify-between gap-3">
    <h1 class="flex items-baseline gap-2.5 font-display text-2xl font-bold uppercase tracking-[0.16em] text-ink text-glow">
      Deck Editor
      {#if isFree}<span class="chamfer-btn bg-magenta/15 px-2 py-0.5 font-display text-[0.6rem] font-bold uppercase tracking-[0.2em] text-magenta" style="--c:4px">Free Duel</span>{/if}
    </h1>
    <div class="flex items-center gap-3">
      {#if !isFree}
        <span class="flex items-baseline gap-1.5 chamfer-btn bg-void-2/60 px-3 py-1.5" style="--c:6px" title="Shards, earned by dismantling cards, spent crafting">
          <span class="text-[10px] uppercase tracking-widest text-ink-dim">◆ Shards</span>
          <span class="font-display text-sm font-bold tabular-nums text-cyan">{shards.toLocaleString()}</span>
        </span>
      {/if}
      <button onclick={() => go(homeRoute)} class="font-display text-xs uppercase tracking-[0.2em] text-ink-dim transition-colors hover:text-cyan">✕ Close</button>
    </div>
  </header>

  <div class="deck-workspace grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[clamp(212px,16vw,264px)_minmax(0,1fr)_clamp(348px,28vw,432px)]">
    <!-- ══ LEFT: CARD PREVIEW ══════════════════════════════════════════ -->
    <aside in:fly={reduced ? { duration: 0 } : { x: -20, duration: 360, opacity: 0 }} class="card-preview hidden min-h-0 lg:block">
      <DataWindow c={14}>
        <div class="flex h-full flex-col items-center px-4 py-4">
          {#if detail}
            <div class="w-full max-w-[200px]"><CardThumb large card={detail} class="glow-cyan" /></div>
            <h3 class="mt-3 line-clamp-2 text-center font-display text-base font-bold leading-tight text-ink">{detail.name}</h3>
            <div class="mt-1 text-center text-[0.7rem] uppercase tracking-wide text-cyan/80">{typeLine(detail)}{detail.level != null ? ` · Lv ${detail.level}` : ""}</div>
            {#if detail.category === "monster" || detail.category === "monster-extra" || detail.category === "token"}
              <div class="text-center font-display text-xs tabular-nums text-gold">ATK {fmtVal(detail.atk)}{detail.def != null ? ` / DEF ${fmtVal(detail.def)}` : ""}</div>
            {/if}
            <p class="mt-2 min-h-0 flex-1 overflow-y-auto scroll-cyber border-t border-cyan-dim/20 pt-2 text-xs leading-snug text-ink-dim">{detail.desc}</p>

            <!-- craft / dismantle (shard economy) — World only; Free Duel has every card unlocked -->
            {#if !isFree}
            {@const dc = detail}
            <div class="mt-2 w-full shrink-0 border-t border-cyan-dim/20 pt-2">
              <div class="mb-1.5 flex items-center justify-between text-[0.7rem]">
                <span class="font-display font-bold uppercase tracking-wide" style="color:{rarityColor(dc.rarity)}">{rarityLabel(dc.rarity)}</span>
                <span class="text-ink-dim">Owned {plainQty(dc.id)}{foilTotal(dc.id) > 0 ? ` +${foilTotal(dc.id)}✦` : ""}{boundQty(dc.id) > 0 ? ` · ${boundQty(dc.id)} bound` : ""}</span>
              </div>
              <div class="grid grid-cols-2 gap-1.5">
                <button
                  onclick={() => craft(dc)}
                  disabled={working || dismantleMode || !craftable(dc.id) || ownedQty(dc.id) >= 3 || shards < craftCost(dc.rarity)}
                  class="chamfer-btn flex flex-col items-center gap-0.5 bg-cyan/12 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wide text-cyan transition-colors hover:bg-cyan/22 disabled:cursor-not-allowed disabled:bg-void-2/50 disabled:text-ink-dim/50"
                  style="--c:5px"
                  title={dismantleMode ? "Exit dismantle mode to craft cards" : !craftable(dc.id) ? `Beyond the ${ERA_LABEL} era — can't be crafted` : ownedQty(dc.id) >= 3 ? "You already own a full playset (3)" : shards < craftCost(dc.rarity) ? "Not enough shards" : "Craft a plain copy"}
                >
                  <span>{craftable(dc.id) ? "Craft" : `Not in ${ERA_LABEL}`}</span><span class="tabular-nums">◆ {craftCost(dc.rarity)}</span>
                </button>
                <button
                  onclick={() => dismantleMode ? addBulkCopy(dc.id, 0) : dismantle(dc)}
                  disabled={working || dismantleablePlain(dc.id) <= 0 || (dismantleMode && selectedQty(dc.id, 0) >= finishCapacity(dc.id, 0))}
                  class="chamfer-btn flex flex-col items-center gap-0.5 bg-magenta/12 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wide text-magenta transition-colors hover:bg-magenta/22 disabled:cursor-not-allowed disabled:bg-void-2/50 disabled:text-ink-dim/50"
                  style="--c:5px"
                  title={dismantleablePlain(dc.id) <= 0 ? (boundQty(dc.id) > 0 ? "Soul-bound starter copies can't be dismantled" : "You don't own a plain copy") : dismantleMode ? "Add one standard copy to the bulk selection" : "Dismantle one plain copy"}
                >
                  <span>{dismantleMode ? "Add plain" : "Dismantle"}</span><span class="tabular-nums">{dismantleMode ? `${selectedQty(dc.id, 0)}/${finishCapacity(dc.id, 0)}` : `+◆ ${dismantleYield(dc.rarity)}`}</span>
                </button>
              </div>
              <!-- foils owned: dismantle a shiny for its (bigger) shard value -->
              {#each ownedGrades(dc.id) as g (g)}
                <button
                  onclick={() => dismantleMode ? addBulkCopy(dc.id, g) : dismantleFoil(dc, g)}
                  disabled={working || (dismantleMode && selectedQty(dc.id, g) >= finishCapacity(dc.id, g))}
                  class="mt-1.5 flex w-full items-center justify-between chamfer-btn px-2 py-1 font-display text-[0.6rem] font-bold uppercase tracking-wide text-ink transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  style="--c:4px;background:color-mix(in oklab, {foilColor(g)} 16%, transparent);box-shadow:inset 0 0 0 1px {foilColor(g)}66"
                  title={dismantleMode ? `Add one ${foilLabel(g)} to the bulk selection` : `Dismantle one ${foilLabel(g)} (you own ${ownedFoilQty(dc.id, g)})`}
                >
                  <span style="color:{foilColor(g)}">✦ {foilLabel(g)} ×{ownedFoilQty(dc.id, g)}</span>
                  <span class="tabular-nums" style="color:{foilColor(g)}">{dismantleMode ? `${selectedQty(dc.id, g)}/${finishCapacity(dc.id, g)}` : `+◆ ${foilDismantleYield(dc.rarity, g)}`}</span>
                </button>
              {/each}
            </div>
            {/if}
          {:else}
            <div class="grid flex-1 place-items-center text-center text-xs text-ink-dim/75">Click a card to inspect it.</div>
          {/if}
        </div>
      </DataWindow>
    </aside>

    <!-- ══ CENTRE: THE DECK ════════════════════════════════════════════ -->
    <section in:fly={reduced ? { duration: 0 } : { y: 16, duration: 360, opacity: 0 }} class="deck-column flex min-h-0 flex-col gap-2">
      <div class="flex shrink-0 flex-wrap items-center gap-2">
        <div class="min-w-[140px] flex-1"><Field bind:value={deckName} placeholder="Deck name" /></div>
        <span class="chamfer-btn bg-void-2/55 px-2.5 py-2 font-display text-[0.66rem] uppercase tracking-wide text-ink-dim" style="--c:5px" title="Monsters · Spells · Traps in Main">
          <span class="text-ink">{split.m}</span>M · <span class="text-lime">{split.s}</span>S · <span class="text-magenta">{split.t}</span>T
        </span>
        <select onchange={(e) => loadDeck(e.currentTarget.value)} class="chamfer-btn bg-void-2/70 px-2.5 py-2 font-display text-[0.66rem] uppercase tracking-wide text-ink outline-none" style="--c:5px">
          <option value="">Load…</option>
          {#each decks as d (d.name)}<option value={d.name}>{d.name}</option>{/each}
        </select>
        <button onclick={autoSort} class="fx-lift chamfer-btn bg-void-2/60 px-2.5 py-2 font-display text-[0.66rem] uppercase tracking-wide text-ink-dim hover:bg-surface-2 hover:text-cyan" style="--c:5px">⤵ Sort</button>
        <button onclick={openImport} class="fx-lift chamfer-btn bg-void-2/60 px-2.5 py-2 font-display text-[0.66rem] uppercase tracking-wide text-ink-dim hover:bg-surface-2 hover:text-cyan" style="--c:5px" title="Import a .ydk / ydke:// deck">⤓ Import</button>
        <button onclick={() => (io = "export")} disabled={deckIsEmpty} class="fx-lift chamfer-btn bg-void-2/60 px-2.5 py-2 font-display text-[0.66rem] uppercase tracking-wide text-ink-dim hover:bg-surface-2 hover:text-cyan disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-void-2/60 disabled:hover:text-ink-dim" style="--c:5px" title="Export this deck as .ydk / ydke://">⤴ Export</button>
        <button onclick={newDeck} class="fx-lift chamfer-btn bg-void-2/60 px-2.5 py-2 font-display text-[0.66rem] uppercase tracking-wide text-ink-dim hover:bg-surface-2 hover:text-ink" style="--c:5px">New</button>
      </div>

      <div data-dropscroll class="min-h-0 flex-1 space-y-2 overflow-y-auto scroll-cyber pr-1">
        {#each ZONES as z (z.key)}
          {@const invalid = z.arr.length > z.max || z.arr.length < z.min}
          {@const over = dnd.drag?.moved && dnd.drag.over === z.key}
          <div
            data-dropzone={z.key}
            class="relative chamfer bg-void-2/25 p-2.5 transition-shadow duration-150 {over
              ? dnd.drag?.accept
                ? 'ring-1 ring-cyan glow-cyan'
                : 'ring-1 ring-magenta/70'
              : ''}"
            style="--c:10px"
          >
            <div class="flex items-baseline justify-between">
              <span class="font-display text-xs font-bold uppercase tracking-[0.18em] {invalid ? 'text-magenta' : 'text-cyan'}">{z.label} Deck</span>
              <span class="font-display text-xs tabular-nums {invalid ? 'text-magenta' : 'text-ink-dim'}">{z.arr.length}{z.key === "main" ? " / 40–60" : " / 15"}</span>
            </div>
            {#if z.key === "main"}
              <div class="mt-1.5 h-1 overflow-hidden rounded-full bg-void/60">
                <div class="h-full transition-all duration-300 {invalid ? 'bg-magenta' : 'bg-cyan'}" style="width:{Math.min(100, (z.arr.length / 60) * 100)}%"></div>
              </div>
            {/if}
            {#if z.arr.length === 0}
              <p class="grid place-items-center py-4 text-center text-[0.7rem] {over && dnd.drag?.accept ? 'text-cyan' : 'text-ink-dim/75'}">
                {over && dnd.drag?.accept ? "Drop to add ↓" : "Empty. Click a card in the pool to add it →"}
              </p>
            {:else}
              <div class="mt-2 grid gap-1" style="grid-template-columns:repeat(auto-fill,minmax(clamp(44px,5.4vw,58px),1fr))">
                {#each z.arr as id, i (z.key + "-" + i)}
                  {#if card(id)}
                    <div data-tile>
                      <CardTile
                        card={card(id)!}
                        owned={1}
                        foil={bestFoil(id)}
                        dim={false}
                        removable
                        lifting={dnd.isLifting(z.key, i)}
                        onpointerdown={(e) => dnd.begin(e, card(id)!, { kind: "deck", zone: z.key, index: i })}
                        oncontextmenu={(e) => { e.preventDefault(); removeAt(z.arr, i); }}
                        onactivate={() => removeAt(z.arr, i)}
                        onhover={() => (detail = card(id) ?? null)}
                      />
                    </div>
                  {/if}
                {/each}
              </div>
            {/if}
            <!-- insertion marker while dragging over this zone -->
            {#if over && dnd.drag?.accept && dnd.drag.caret}
              <div
                class="pointer-events-none absolute z-10 w-0.5 -translate-x-1/2 rounded-full bg-cyan glow-cyan"
                style="left:{dnd.drag.caret.left}px; top:{dnd.drag.caret.top}px; height:{dnd.drag.caret.h}px"
              ></div>
            {/if}
          </div>
        {/each}
      </div>

      <NeonButton full disabled={!legal} onclick={save}>
        {legal ? "Save Deck" : main.length < 40 ? `Main needs ${40 - main.length} more` : "Fix deck to save"}
      </NeonButton>
    </section>

    <!-- ══ RIGHT: CARD POOL ════════════════════════════════════════════ -->
    <aside in:fly={reduced ? { duration: 0 } : { x: 20, duration: 360, opacity: 0 }} class="card-pool flex min-h-0 flex-col gap-2">
      <!-- scope -->
      <div class="grid shrink-0 gap-1 chamfer bg-void-2/50 p-1 {isFree ? 'grid-cols-2' : 'grid-cols-[1fr_1fr_auto]'}" style="--c:8px">
        <button onclick={() => (viewAll = false)} class="chamfer-btn py-1.5 font-display text-[0.7rem] font-semibold uppercase tracking-wide transition-colors {!viewAll ? 'bg-surface-2 text-cyan glow-cyan' : 'text-ink-dim hover:bg-void-2/80 hover:text-ink'}" style="--c:5px">My Collection · {ownedCards.length}</button>
        <button onclick={showAllCards} class="chamfer-btn py-1.5 font-display text-[0.7rem] font-semibold uppercase tracking-wide transition-colors {viewAll ? 'bg-surface-2 text-cyan glow-cyan' : 'text-ink-dim hover:bg-void-2/80 hover:text-ink'}" style="--c:5px">All Cards</button>
        {#if !isFree}
          <button
            onclick={toggleDismantleMode}
            aria-pressed={dismantleMode}
            class="touch:min-h-11 chamfer-btn px-2.5 py-1.5 font-display text-[0.66rem] font-bold uppercase tracking-wide transition-colors {dismantleMode ? 'bg-magenta/18 text-magenta glow-magenta' : 'text-ink-dim hover:bg-magenta/10 hover:text-magenta'}"
            style="--c:5px"
            title="Select and dismantle several cards at once"
          >{dismantleMode ? "Done" : "Dismantle"}</button>
        {/if}
      </div>

      {#if dismantleMode}
        <div class="shrink-0 chamfer border border-cyan-dim/35 bg-surface/90 p-2" style="--c:8px">
          <div class="flex items-center gap-1.5">
            <label class="flex min-w-0 flex-1 items-center gap-1.5">
              <span class="shrink-0 font-display text-[0.62rem] font-semibold uppercase tracking-wide text-ink-dim">Finish</span>
              <select
                value={bulkFinish}
                onchange={(e) => chooseBulkFinish(Number(e.currentTarget.value))}
                aria-label="Card finish to select"
                class="min-w-0 flex-1 chamfer-btn bg-void-2/80 px-2 py-1.5 font-display text-[0.64rem] uppercase tracking-wide text-cyan outline-none"
                style="--c:4px"
              >
                {#each FINISHES as finish (finish.grade)}
                  <option value={finish.grade}>{finish.label}</option>
                {/each}
              </select>
            </label>
            <button
              onclick={() => { extrasConfirm = true; bulkConfirm = false; }}
              disabled={working || loading || extraCopies <= 0}
              class="touch:min-h-11 chamfer-btn bg-magenta/12 px-2.5 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wide text-magenta transition-colors hover:bg-magenta/22 disabled:cursor-not-allowed disabled:bg-void-2/60 disabled:text-ink-dim/50"
              style="--c:4px"
              title="Dismantle every standard copy above three; foil cards are never included"
            >{extraCopies > 0 ? `Extras · ${extraCopies}` : "No extras"}</button>
          </div>
          {#if extrasConfirm}
            <div class="mt-2 border-t border-magenta/25 pt-2" aria-live="polite">
              <p class="text-[0.68rem] leading-snug text-ink">Dismantle <strong>{extraCopies} extra standard {extraCopies === 1 ? "copy" : "copies"}</strong> for <span class="font-semibold text-gold">+◆ {extraShardGain.toLocaleString()}</span>? Foils stay protected.</p>
              <div class="mt-1.5 grid grid-cols-2 gap-1.5">
                <button onclick={() => (extrasConfirm = false)} disabled={working} class="touch:min-h-11 chamfer-btn bg-void-2/70 px-2 py-1.5 font-display text-[0.62rem] font-semibold uppercase tracking-wide text-ink-dim hover:text-ink disabled:opacity-50" style="--c:4px">Keep cards</button>
                <button onclick={submitExtraDismantle} disabled={working || extraCopies <= 0} class="touch:min-h-11 chamfer-btn bg-magenta/18 px-2 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wide text-magenta hover:bg-magenta/28 disabled:opacity-50" style="--c:4px">{working ? "Dismantling…" : `Dismantle ${extraCopies}`}</button>
              </div>
            </div>
          {:else}
            <p class="phone:hidden mt-1.5 text-[0.62rem] leading-snug text-ink-dim">Choose a finish, then select cards. The Extras action always keeps three standard copies and never selects foils.</p>
          {/if}
        </div>
      {/if}

      <Field bind:value={q} placeholder="Search name or card text…" />

      <!-- filter bar: category frame (chips) → contextual sub-filters (labelled selects) -->
      <div class="shrink-0 space-y-1.5 {dismantleMode ? 'phone:hidden' : ''}">
        <div class="flex flex-wrap items-center gap-1">
          {#each CATS as c (c.v)}
            <button onclick={() => pickCat(c.v)} class="chamfer-btn px-2.5 py-1 font-display text-[0.64rem] font-semibold uppercase tracking-wide transition-colors {cat === c.v ? 'bg-surface-2 text-cyan glow-cyan' : 'bg-void-2/50 text-ink-dim hover:bg-void-2/90 hover:text-ink'}" style="--c:4px">{c.label}</button>
          {/each}
          <select bind:value={sortBy} aria-label="Sort cards" class="{selectCls} ml-auto text-ink-dim hover:text-ink" style="--c:4px">
            <option value="name">Sort: A–Z</option>
            <option value="atk">Sort: ATK ↓</option>
            <option value="level">Sort: Level ↓</option>
            <option value="rarity">Sort: Shards ↓</option>
          </select>
        </div>
        <div class="flex flex-wrap items-center gap-1">
          <select bind:value={type} aria-label="Card type" class="{selectCls} flex-1 {type ? 'text-cyan' : 'text-ink-dim hover:text-ink'}" style="--c:4px">
            <option value="">All Types</option>
            {#each typeOpts as t (t)}<option value={t}>{t}</option>{/each}
          </select>
          {#if isMonsterCat}
            <select bind:value={attr} aria-label="Attribute" class="{selectCls} flex-1 {attr ? 'text-cyan' : 'text-ink-dim hover:text-ink'}" style="--c:4px">
              <option value="">All Attributes</option>
              {#each ATTRS as a (a)}<option value={a}>{a}</option>{/each}
            </select>
            <select bind:value={race} aria-label="Monster type/race" class="{selectCls} flex-1 {race ? 'text-cyan' : 'text-ink-dim hover:text-ink'}" style="--c:4px">
              <option value="">All Races</option>
              {#each RACES as rc (rc)}<option value={rc}>{rc}</option>{/each}
            </select>
            <select bind:value={level} aria-label="Level / Rank" class="{selectCls} flex-1 {level !== '' ? 'text-cyan' : 'text-ink-dim hover:text-ink'}" style="--c:4px">
              <option value="">Any Level</option>
              {#each Array(12) as _, i (i)}<option value={i + 1}>Lv {i + 1}</option>{/each}
            </select>
          {/if}
        </div>
      </div>

      <!-- pool card grid (also the drop target for pulling deck cards out) -->
      <div
        data-dropscroll
        data-dropzone="pool"
        class="min-h-0 flex-1 overflow-y-auto scroll-cyber chamfer bg-void-2/25 p-2 transition-shadow duration-150 {dnd.drag?.moved && dnd.drag.over === 'pool' && dnd.drag.accept ? 'ring-1 ring-magenta glow-magenta' : ''}"
        style="--c:10px"
      >
        {#if loading}
          <p class="grid h-full place-items-center font-display text-sm text-ink-dim">Loading…</p>
        {:else if errored}
          <p class="grid h-full place-items-center font-display text-sm text-magenta">Couldn't load cards.</p>
        {:else if results.length === 0}
          <p class="grid h-full place-items-center px-4 text-center font-display text-sm text-ink-dim">
            {!viewAll && ownedCards.length === 0 ? "You don't own any cards yet." : "No cards match, try All Cards."}
          </p>
        {:else}
          <div class="grid gap-1.5" style="grid-template-columns:repeat(auto-fill,minmax(72px,1fr))">
            {#each sorted as c (c.id)}
              <CardTile
                card={c}
                owned={dismantleMode ? finishCapacity(c.id) : isFree ? 3 : ownedQty(c.id)}
                used={dismantleMode ? selectedQty(c.id) : count(c.id)}
                foil={dismantleMode ? (bulkFinish > 0 && ownedFoilQty(c.id, bulkFinish) > 0 ? bulkFinish : 0) : bestFoil(c.id)}
                selecting={dismantleMode}
                selected={dismantleMode && selectedQty(c.id) > 0}
                onclick={dismantleMode ? () => toggleBulk(c) : undefined}
                onpointerdown={dismantleMode ? undefined : (e) => dnd.begin(e, c, poolOrigin)}
                oncontextmenu={dismantleMode ? undefined : (e) => { e.preventDefault(); removeOne(c.id); }}
                onactivate={dismantleMode ? undefined : () => add(c)}
                onhover={() => (detail = c)}
              />
            {/each}
          </div>
          {#if viewAll && (catalog.data?.total ?? 0) > limit}
            <div class="mt-2.5 grid place-items-center">
              <button onclick={() => (limit += 96)} class="fx-lift fx-sweep chamfer-btn bg-surface-2 px-4 py-1.5 font-display text-[0.7rem] font-semibold uppercase tracking-wider text-ink hover:text-cyan hover:glow-cyan" style="--c:6px">Load more · {limit}/{catalog.data?.total}</button>
            </div>
          {/if}
        {/if}
      </div>

      {#if dismantleMode && !extrasConfirm}
        <div class="shrink-0 chamfer border border-cyan-dim/30 bg-void-2/92 p-2" style="--c:8px" aria-live="polite">
          {#if bulkConfirm}
            <p class="text-[0.7rem] leading-snug text-ink">Dismantle <strong>{bulkCopies} selected {bulkCopies === 1 ? "copy" : "copies"}</strong> for <span class="font-semibold text-gold">+◆ {bulkShardGain.toLocaleString()}</span>? This cannot be undone.</p>
            <div class="mt-1.5 grid grid-cols-2 gap-1.5">
              <button onclick={() => (bulkConfirm = false)} disabled={working} class="touch:min-h-11 chamfer-btn bg-surface-2 px-2 py-1.5 font-display text-[0.62rem] font-semibold uppercase tracking-wide text-ink-dim hover:text-ink disabled:opacity-50" style="--c:4px">Review selection</button>
              <button onclick={submitBulkDismantle} disabled={working || bulkCopies <= 0} class="touch:min-h-11 chamfer-btn bg-magenta/18 px-2 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wide text-magenta hover:bg-magenta/28 disabled:opacity-50" style="--c:4px">{working ? "Dismantling…" : `Dismantle ${bulkCopies}`}</button>
            </div>
          {:else}
            <div class="flex items-baseline justify-between gap-2">
              <p class="min-w-0 truncate font-display text-[0.7rem] font-semibold uppercase tracking-wide text-ink">
                {bulkCardCount} {bulkCardCount === 1 ? "card" : "cards"} · {bulkCopies} {bulkCopies === 1 ? "copy" : "copies"}
                <span class="ml-1 text-gold">+◆ {bulkShardGain.toLocaleString()}</span>
              </p>
              <button onclick={() => (bulkSelection = {})} disabled={bulkCopies <= 0 || working} class="font-display text-[0.6rem] font-semibold uppercase tracking-wide text-ink-dim hover:text-cyan disabled:opacity-40">Clear</button>
            </div>
            {#if detail && finishCapacity(detail.id) > 0}
              {@const selectedDetail = detail}
              <div class="mt-1.5 flex items-center gap-1.5 border-t border-cyan-dim/20 pt-1.5">
                <span class="min-w-0 flex-1 truncate text-[0.66rem] text-ink-dim" title={selectedDetail.name}>{selectedDetail.name} · {finishLabel()}</span>
                <button onclick={() => setBulkQty(selectedDetail.id, bulkFinish, selectedQty(selectedDetail.id) - 1)} disabled={working || selectedQty(selectedDetail.id) <= 0} aria-label="Select one fewer copy of {selectedDetail.name}" class="touch:size-11 grid size-7 place-items-center chamfer-btn bg-surface-2 font-display text-sm font-bold text-cyan disabled:text-ink-dim/40" style="--c:3px">−</button>
                <span class="w-10 text-center font-display text-[0.7rem] font-bold tabular-nums text-ink">{selectedQty(selectedDetail.id)}/{finishCapacity(selectedDetail.id)}</span>
                <button onclick={() => addBulkCopy(selectedDetail.id, bulkFinish)} disabled={working || selectedQty(selectedDetail.id) >= finishCapacity(selectedDetail.id)} aria-label="Select one more copy of {selectedDetail.name}" class="touch:size-11 grid size-7 place-items-center chamfer-btn bg-surface-2 font-display text-sm font-bold text-cyan disabled:text-ink-dim/40" style="--c:3px">+</button>
              </div>
            {:else}
              <p class="mt-1 text-[0.62rem] text-ink-dim">Select a card to set its quantity.</p>
            {/if}
            <button
              onclick={() => { bulkConfirm = true; extrasConfirm = false; }}
              disabled={working || bulkCopies <= 0}
              class="touch:min-h-11 mt-1.5 w-full chamfer-btn bg-magenta/14 px-3 py-1.5 font-display text-[0.64rem] font-bold uppercase tracking-wide text-magenta transition-colors hover:bg-magenta/24 disabled:cursor-not-allowed disabled:bg-surface-2/60 disabled:text-ink-dim/45"
              style="--c:5px"
            >Dismantle selected</button>
          {/if}
        </div>
      {/if}
    </aside>
  </div>

  <!-- floating drag ghost (follows the pointer) -->
  {#if dnd.drag?.moved}
    {@const d = dnd.drag}
    {@const removing = d.over === "pool" && d.accept}
    <div class="pointer-events-none fixed z-[60]" style="left:{d.x - d.ox}px; top:{d.y - d.oy}px; width:{d.w}px">
      <div
        class="relative aspect-[59/86] rotate-3 overflow-hidden chamfer-btn shadow-2xl shadow-black/60 transition-[box-shadow] duration-100 {removing
          ? 'glow-magenta ring-2 ring-magenta'
          : d.accept
            ? 'glow-cyan ring-2 ring-cyan'
            : 'opacity-85 ring-2 ring-white/15'}"
        style="--c:5px; transform-origin:center"
      >
        <img src={cardImage(d.card.id)} alt="" draggable="false" class="size-full object-cover" />
        {#if removing}
          <div class="absolute inset-0 grid place-items-center bg-magenta/35">
            <span class="grid size-6 place-items-center rounded-full bg-void/85 text-sm font-bold text-magenta">✕</span>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  <!-- Import / Export deck (.ydk + ydke://) -->
  {#if io}
    <div class="mobile-dialog-host fixed inset-0 z-[70] grid place-items-center p-4" role="presentation">
      <button class="modal-scrim absolute inset-0 bg-void/50" onclick={() => (io = null)} aria-label="Close"></button>
      <div use:draggableDialog role="dialog" aria-modal="true" aria-label={io === "import" ? "Import deck" : "Export deck"} class="mobile-full-dialog relative w-[min(560px,94vw)]" transition:fly={{ y: 16, duration: reduced ? 0 : 220 }}>
        <DataWindow c={16} fill class="mobile-dialog-panel">
          <div class="dialog-drag-handle flex items-center justify-between border-b border-cyan-dim/40 bg-surface-2/70 px-5 py-2.5" data-dialog-drag-handle>
            <span class="font-display text-[0.72rem] font-semibold uppercase tracking-[0.26em] text-cyan">
              {io === "import" ? "Import Deck" : "Export Deck"}
            </span>
            <button onclick={() => (io = null)} class="text-ink-dim transition-colors hover:text-cyan" aria-label="Close">
              <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </button>
          </div>

          <div class="mobile-dialog-scroll space-y-3 overflow-y-auto scroll-cyber p-4">
            {#if io === "export"}
              <p class="text-xs text-ink-dim">Standard .ydk / ydke:// deck format.</p>
              <textarea readonly value={exportYdk} class="h-52 w-full resize-none chamfer bg-void/60 p-3 font-mono text-[0.72rem] leading-relaxed text-ink-dim scroll-cyber outline-none" style="--c:8px"></textarea>
              <div class="flex flex-wrap gap-2">
                <NeonButton onclick={() => copyExport("ydke")}>Copy ydke://</NeonButton>
                <button onclick={() => copyExport("ydk")} class="chamfer-btn bg-void-2/70 px-4 py-2.5 font-display text-xs font-semibold uppercase tracking-wide text-ink transition-colors hover:text-cyan" style="--c:8px">Copy .ydk text</button>
                <button onclick={downloadYdk} class="chamfer-btn bg-void-2/70 px-4 py-2.5 font-display text-xs font-semibold uppercase tracking-wide text-ink transition-colors hover:text-cyan" style="--c:8px">Download .ydk</button>
              </div>
            {:else}
              <p class="text-xs text-ink-dim">
                Paste a .ydk list or an <span class="font-mono text-cyan">ydke://</span> URL, or load a .ydk file.
                {#if isFree}<span class="text-lime">Any card is allowed.</span>{:else}<span class="text-gold">World decks must stay within the {ERA_LABEL} era.</span>{/if}
              </p>
              <textarea
                bind:value={importText}
                spellcheck="false"
                placeholder={"ydke://...   — or —\n#main\n89631139\n..."}
                class="h-44 w-full resize-none chamfer bg-void/60 p-3 font-mono text-[0.72rem] leading-relaxed text-ink scroll-cyber outline-none placeholder:text-ink-dim/70"
                style="--c:8px"
              ></textarea>
              {#if importErr}
                <p class="chamfer bg-magenta/12 px-3 py-2 text-xs text-magenta" style="--c:6px">{importErr}</p>
              {/if}
              <div class="flex flex-wrap items-center gap-2">
                <label class="chamfer-btn cursor-pointer bg-void-2/70 px-4 py-2.5 font-display text-xs font-semibold uppercase tracking-wide text-ink transition-colors hover:text-cyan" style="--c:8px">
                  Load .ydk file
                  <input type="file" accept=".ydk,text/plain" class="hidden" onchange={onYdkFile} />
                </label>
                <div class="flex-1"></div>
                <NeonButton onclick={doImport}>Import Deck</NeonButton>
              </div>
            {/if}
          </div>
        </DataWindow>
      </div>
    </div>
  {/if}

  {#if toast}
    <div transition:fly={{ y: 12, duration: reduced ? 0 : 200 }} class="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
      <Toast kind={toast.kind}>{toast.text}</Toast>
    </div>
  {/if}
</div>

<style>
  /* A landscape phone is wide enough for the editor's two real work surfaces.
     Keeping them side-by-side avoids a page-length vertical stack and gives
     each pane its own deliberate scroller. */
  :global(.phone) .deck-editor-shell {
    gap: 0.45rem;
    padding:
      max(0.4rem, env(safe-area-inset-top))
      max(0.45rem, env(safe-area-inset-right))
      max(0.4rem, env(safe-area-inset-bottom))
      max(0.45rem, env(safe-area-inset-left));
  }
  :global(.phone) .deck-editor-header h1 {
    font-size: 1.1rem;
    letter-spacing: 0.1em;
  }
  :global(.phone) .deck-editor-header > div {
    gap: 0.45rem;
  }
  :global(.phone) .deck-workspace {
    grid-template-columns: minmax(0, 1fr) minmax(19rem, 43vw);
    gap: 0.5rem;
  }
  :global(.phone) .card-preview {
    display: none !important;
  }
  :global(.phone) .deck-column,
  :global(.phone) .card-pool {
    overflow: hidden;
  }
  :global(.phone) .deck-workspace button,
  :global(.phone) .deck-workspace select {
    min-height: 36px;
  }
</style>
